import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, Trophy, CalendarDays, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

// Import the competitions data
import { activeCompetitions } from "./components/CompetitionsSection";

// Image component with fallback
function CompetitionImage({ src, alt, fallbackSrc }) {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  
  const handleError = () => {
    if (!hasError && fallbackSrc) {
      setImgSrc(fallbackSrc);
      setHasError(true);
    }
  };
  
  return (
    <img 
      src={imgSrc} 
      alt={alt} 
      onError={handleError}
      className="w-full h-full object-cover object-top"
    />
  );
}

// Additional mock competitions for the page
const allCompetitions = [
  ...activeCompetitions
];

// Reusable CountdownTimer component
function CountdownTimer({ targetDate }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Check if the target date is in the past
  if (targetDate < currentTime) {
    return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-500/50 px-3 py-1 font-medium rounded-md">Competition Ended</Badge>;
  }
  
  // For actual live competitions
  const competitionEnd = new Date(targetDate.getTime() + (24 * 60 * 60 * 1000));
  if (currentTime >= targetDate && currentTime <= competitionEnd) {
    return <Badge variant="destructive" className="text-sm font-medium px-3 py-1">Live Now!</Badge>;
  }
  
  // Calculate countdown for future competitions
  const timeLeft = targetDate - currentTime;
  const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((timeLeft / (1000 * 60)) % 60);
  const seconds = Math.floor((timeLeft / 1000) % 60);

  // For competitions with TBD date
  if (targetDate.getFullYear() === 2025 && targetDate.getMonth() === 5 && targetDate.getDate() === 1) { // June 1, 2025
    return (
      <div className="flex flex-col items-center gap-1">
        <Badge variant="outline" className="bg-transparent border-yellow-500/20 text-yellow-600 shadow-none">Starts in</Badge>
        <div className="text-sm font-medium text-yellow-600">TBD</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <Badge variant="outline" className="bg-transparent border-yellow-500/20 text-yellow-600 shadow-none">Starts in</Badge>
      <div className="grid grid-flow-col gap-1 text-center auto-cols-max">
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{days}</span>
          <span className="text-xs text-muted-foreground">days</span>
        </div>
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{hours}</span>
          <span className="text-xs text-muted-foreground">hrs</span>
        </div>
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{minutes}</span>
          <span className="text-xs text-muted-foreground">min</span>
        </div>
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{seconds}</span>
          <span className="text-xs text-muted-foreground">sec</span>
        </div>
      </div>
    </div>
  );
}

// Get all unique categories
const categories = ["All", ...new Set(allCompetitions.map(comp => comp.category))];

export default function AllCompetitions() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("date");
  const [filteredCompetitions, setFilteredCompetitions] = useState(allCompetitions);
  
  // Filter and sort competitions based on search, category, and sort criteria
  useEffect(() => {
    let result = [...allCompetitions];
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter(comp => 
        comp.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comp.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by category
    if (selectedCategory !== "All") {
      result = result.filter(comp => comp.category === selectedCategory);
    }
    
    // Sort competitions
    if (sortBy === "date") {
      // First show active competitions, then by date
      result.sort((a, b) => {
        const aIsEnded = a.status === "ended" || a.targetDate < new Date();
        const bIsEnded = b.status === "ended" || b.targetDate < new Date();
        
        if (aIsEnded && !bIsEnded) return 1;
        if (!aIsEnded && bIsEnded) return -1;
        
        return a.targetDate - b.targetDate;
      });
    } else if (sortBy === "prize") {
      result.sort((a, b) => {
        const prizeA = a.prize.replace(/[^0-9]/g, "");
        const prizeB = b.prize.replace(/[^0-9]/g, "");
        return parseInt(prizeB || 0) - parseInt(prizeA || 0);
      });
    } else if (sortBy === "title") {
      result.sort((a, b) => a.title.localeCompare(b.title));
    }
    
    setFilteredCompetitions(result);
  }, [searchTerm, selectedCategory, sortBy]);

  return (
    <div className="pb-20">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 py-24">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              All Competitions
            </h1>
            <p className="text-white/90 text-lg mb-8">
              Browse all our exciting competitions and find the perfect opportunity to showcase your talents.
            </p>
            <p className="text-white font-medium mb-4">Download the app to participate</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://apps.apple.com/ca/app/pagent/id6670614673" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                <div className="rounded-lg overflow-hidden" style={{ maxWidth: '180px' }}>
                  <img 
                    src="/images/AppStoreFig.png" 
                    alt="Download on App Store" 
                    className="h-auto w-full object-contain"
                  />
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.pagent" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                <div className="rounded-lg overflow-hidden" style={{ maxWidth: '180px' }}>
                  <img 
                    src="/images/GoogleStoreFig.png" 
                    alt="Get it on Google Play" 
                    className="h-auto w-full object-contain"
                  />
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filter Section */}
      <div className="bg-muted py-8">
        <div className="container">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-auto flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search competitions..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <div className="flex items-center gap-2">
                <Filter className="text-muted-foreground h-4 w-4" />
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Sort by:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date">Date (Soonest)</SelectItem>
                    <SelectItem value="prize">Prize (Highest)</SelectItem>
                    <SelectItem value="title">Title (A-Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Competitions List */}
      <div className="container py-12">
        <Tabs defaultValue="grid" className="w-full">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold">
              {filteredCompetitions.length} Competition{filteredCompetitions.length !== 1 ? 's' : ''} Found
            </h2>
            <TabsList>
              <TabsTrigger value="grid">Grid View</TabsTrigger>
              <TabsTrigger value="list">List View</TabsTrigger>
            </TabsList>
          </div>
          
          {filteredCompetitions.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium mb-2">No competitions found</h3>
              <p className="text-muted-foreground mb-6">Try adjusting your search or filter criteria</p>
              <Button onClick={() => {
                setSearchTerm("");
                setSelectedCategory("All");
              }}>
                Reset Filters
              </Button>
            </div>
          ) : (
            <>
              <TabsContent value="grid" className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredCompetitions.map(competition => (
                    <Card key={competition.id} className="overflow-hidden h-full flex flex-col">
                      <div className="h-48 bg-muted flex items-center justify-center relative">
                        {competition.image ? (
                          <CompetitionImage 
                            src={competition.image} 
                            alt={competition.title} 
                            fallbackSrc={competition.fallbackImage || "/images/makeupcompetition1.png"}
                          />
                        ) : (
                          <div className="text-4xl font-bold text-yellow-500">{competition.category}</div>
                        )}
                        <Badge 
                          className="absolute top-4 left-4 bg-yellow-500 text-white border-0 shadow-md font-bold px-4 py-2 text-base"
                        >
                          {competition.prize}
                        </Badge>
                      </div>
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className="flex-grow">
                          <Badge 
                            className="bg-yellow-500/20 text-yellow-600 border-0 shadow-none font-medium px-3 py-1 text-sm inline-block mb-1"
                          >
                            {competition.category}
                          </Badge>
                          
                          <h3 className="text-xl font-bold mb-3">{competition.title}</h3>
                          
                          <p className="text-sm text-muted-foreground mb-6">
                            {competition.description}
                          </p>
                        </div>
                        
                        <div className="mt-auto space-y-6">
                          <div className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-yellow-500" />
                            <span className="text-sm">{competition.date}</span>
                          </div>
                          
                          <div className="flex justify-center mb-6">
                            <CountdownTimer targetDate={competition.targetDate} />
                          </div>
                          
                          <Link to={`/competitions/${competition.id}`} className="block mt-4">
                            <Button variant="default" className="w-full gap-1 bg-yellow-500 hover:bg-yellow-600 text-white">
                              View Details <ArrowRight className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="list" className="mt-0">
                <div className="space-y-4">
                  {filteredCompetitions.map(competition => (
                    <Card key={competition.id} className="overflow-hidden">
                      <div className="flex flex-col md:flex-row">
                        <div className="w-full md:w-48 h-48 md:h-auto bg-muted flex-shrink-0 relative">
                          {competition.image ? (
                            <CompetitionImage 
                              src={competition.image} 
                              alt={competition.title} 
                              fallbackSrc={competition.fallbackImage || "/images/makeupcompetition1.png"}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-yellow-500/20 text-yellow-600 text-xl font-bold">
                              {competition.category}
                            </div>
                          )}
                          <Badge 
                            className="absolute top-4 left-4 bg-yellow-500 text-white border-0 shadow-md font-bold px-4 py-2 text-base md:hidden"
                          >
                            {competition.prize}
                          </Badge>
                        </div>
                        <CardContent className="p-6 flex flex-col justify-between w-full">
                          <div>
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <Badge 
                                className="bg-yellow-500/20 text-yellow-600 border-0 shadow-none font-medium px-3 py-1 text-sm"
                              >
                                {competition.category}
                              </Badge>
                              <Badge 
                                className="bg-yellow-500 text-white border-0 shadow-md font-bold px-4 py-2 text-base hidden md:inline-flex"
                              >
                                {competition.prize}
                              </Badge>
                            </div>
                            
                            <h3 className="text-xl font-bold mb-2">{competition.title}</h3>
                            
                            <p className="text-sm text-muted-foreground mb-4">
                              {competition.description}
                            </p>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
                            <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                              <div className="flex items-center gap-2">
                                <CalendarDays className="h-4 w-4 text-yellow-500" />
                                <span className="text-sm">{competition.date}</span>
                              </div>
                              
                              <div className="flex justify-center mb-0">
                                <CountdownTimer targetDate={competition.targetDate} />
                              </div>
                            </div>
                            
                            <Link to={`/competitions/${competition.id}`} className="block">
                              <Button variant="default" className="gap-1 bg-yellow-500 hover:bg-yellow-600 text-white">
                                View Details <ArrowRight className="h-4 w-4" />
                              </Button>
                            </Link>
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
      
      {/* CTA Section */}
      <div className="bg-muted/30 py-16">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold tracking-tighter">Ready to Participate?</h2>
            <p className="text-xl text-muted-foreground">
              Download our app today and start showcasing your talents to the world.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <a href="https://apps.apple.com/ca/app/pagent/id6670614673" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                <div className="rounded-lg overflow-hidden" style={{ maxWidth: '180px' }}>
                  <img 
                    src="/images/AppStoreFig.png" 
                    alt="Download on App Store" 
                    className="h-auto w-full object-contain"
                  />
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.pagent" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                <div className="rounded-lg overflow-hidden" style={{ maxWidth: '180px' }}>
                  <img 
                    src="/images/GoogleStoreFig.png" 
                    alt="Get it on Google Play" 
                    className="h-auto w-full object-contain"
                  />
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 